import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { ShieldCheck, PiggyBank, Landmark, ArrowRight, BookOpen, Compass } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/client/education")({
  head: () => ({ meta: [{ title: "Education — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <Education />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface Resource { id: string; title: string; summary: string | null; category: string | null; slug: string; }

const PATHWAYS = [
  {
    key: "protection",
    icon: ShieldCheck,
    eyebrow: "Pathway 01",
    title: "Protect the Household",
    blurb: "Begin here. Make sure income, life, and disability coverage match the family you have today.",
    steps: [
      "Read: How much life insurance a household actually needs",
      "Read: Term vs. permanent — when each belongs in a plan",
      "Action: Confirm beneficiaries on every policy",
      "Action: Book a coverage review with your Strategist",
    ],
    category: "Protection",
  },
  {
    key: "retirement",
    icon: PiggyBank,
    eyebrow: "Pathway 02",
    title: "Build the Second Act",
    blurb: "Move from saving to income. Understand the vehicles that turn assets into a paycheck for life.",
    steps: [
      "Read: The three buckets of retirement income",
      "Read: How tax-advantaged accounts stack",
      "Action: Run the Retirement calculator",
      "Action: Map your target retirement age in Family Snapshot",
    ],
    category: "Retirement",
  },
  {
    key: "legacy",
    icon: Landmark,
    eyebrow: "Pathway 03",
    title: "Transfer the Legacy",
    blurb: "Estate documents, trusts, and the family meeting that turns a balance sheet into an inheritance.",
    steps: [
      "Read: Will, trust, and beneficiary — which does what",
      "Read: The family meeting playbook",
      "Action: Upload estate documents to the Vault",
      "Action: Walk a successor through Vault access",
    ],
    category: "Legacy",
  },
] as const;

function Education() {
  const { user } = useAuth();
  const [items, setItems] = useState<Resource[]>([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState<string>("all");
  const [completedLessons, setCompletedLessons] = useState(0);
  const [totalLessons, setTotalLessons] = useState(0);
  const [hasStrategist, setHasStrategist] = useState<boolean | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("academy_resources")
        .select("id,title,summary,category,slug,published,audience")
        .eq("published", true)
        .in("audience", ["all", "client"])
        .order("created_at", { ascending: false });
      setItems((data ?? []) as Resource[]);
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [lessonsRes, progressRes] = await Promise.all([
        supabase.from("academy_lessons").select("id", { count: "exact", head: true }).eq("published", true),
        // Lesson completion is written as status "complete" with a completed_at
        // stamp (see academy.functions). Filtering on "completed" matched
        // nothing, so this counter always read 0.
        supabase
          .from("academy_progress")
          .select("lesson_id")
          .eq("user_id", user.id)
          .not("completed_at", "is", null),
      ]);
      setTotalLessons(lessonsRes.count ?? 0);
      setCompletedLessons((progressRes.data ?? []).length);
    })();
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("clients")
        .select("assigned_agent_id")
        .eq("user_id", user.id)
        .maybeSingle();
      setHasStrategist(!!data?.assigned_agent_id);
    })();
  }, [user?.id]);

  const pct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const cats = Array.from(new Set(items.map(i => i.category).filter(Boolean))) as string[];
  const filtered = items.filter(i => (cat === "all" || i.category === cat) && (!search || i.title.toLowerCase().includes(search.toLowerCase())));

  return (
    <PortalPage eyebrow="Family Library" title="Education" intro="A guided path through household stewardship — written for families, not finance pros.">
      {/* Where to start */}
      <div className="mb-10 bg-navy-900 text-cream-50 rounded-[4px] p-6 md:p-8 shadow-soft">
        <div className="flex items-start gap-4">
          <div className="hidden md:flex h-12 w-12 flex-none items-center justify-center rounded-full bg-bronze-600/20 border border-bronze-600/40">
            <Compass className="h-5 w-5 text-bronze-400" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <Eyebrow color="cream">Where to start</Eyebrow>
            <h3 className="mt-3 font-display text-[22px] md:text-[26px] leading-snug">
              Work through the three pathways below in order.
            </h3>
            <p className="mt-2 font-sans text-[14px] text-cream-50/80 max-w-2xl">
              Each pathway is 2 short reads and 2 small actions. Most families finish one in a week.
              {hasStrategist
                ? " Your Strategist is notified as you complete lessons and can pick up the conversation where you left off."
                : " Once a Strategist is assigned to your household, they'll see your progress and can pick up the conversation where you left off."}
            </p>
            <div className="mt-5 flex items-baseline justify-between max-w-md">
              <span className="font-sans text-[12px] uppercase tracking-[0.18em] text-cream-50/70">Lesson progress</span>
              <span className="font-display text-[20px]">{completedLessons} / {totalLessons}</span>
            </div>
            <Progress value={pct} className="mt-2 h-1.5 bg-cream-50/10 [&>div]:bg-bronze-400 max-w-md" />
          </div>
        </div>
      </div>

      {/* Pathways */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        {PATHWAYS.map(p => {
          const Icon = p.icon;
          return (
            <div key={p.key} className="bg-white border border-bronze-600/20 border-t-[3px] border-t-bronze-600 rounded-[4px] p-6 shadow-soft flex flex-col">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-navy-800">
                <Icon className="h-5 w-5 text-bronze-400" strokeWidth={1.6} />
              </div>
              <p className="mt-4 font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">{p.eyebrow}</p>
              <h3 className="mt-1 font-display text-navy-800 text-[20px] leading-snug">{p.title}</h3>
              <p className="mt-2 font-sans text-[13px] text-charcoal-500 leading-relaxed">{p.blurb}</p>
              <ol className="mt-4 space-y-2 flex-1">
                {p.steps.map((s, i) => (
                  <li key={s} className="flex gap-3 font-sans text-[13px] text-charcoal-900">
                    <span className="font-display text-bronze-600 text-[14px] leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <span className="leading-snug">{s}</span>
                  </li>
                ))}
              </ol>
              <button
                onClick={() => setCat(p.category)}
                className="mt-5 inline-flex items-center gap-2 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-600 hover:text-navy-800 self-start"
              >
                Open this pathway <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-6">
        <BookOpen className="h-4 w-4 text-bronze-600" />
        <Eyebrow>The Library</Eyebrow>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Input placeholder="Search articles…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs bg-white border-bronze-600/30" />
        <button onClick={() => setCat("all")} className={`px-3 py-1.5 text-[12px] uppercase tracking-wider rounded-[4px] border ${cat === "all" ? "bg-navy-800 text-cream-50 border-navy-800" : "border-bronze-600/40 text-charcoal-900"}`}>All</button>
        {cats.map(c => (
          <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 text-[12px] uppercase tracking-wider rounded-[4px] border ${cat === c ? "bg-navy-800 text-cream-50 border-navy-800" : "border-bronze-600/40 text-charcoal-900"}`}>{c}</button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="bg-cream-50/60 border border-dashed border-bronze-600/30 rounded-[4px] p-8 text-center">
          <p className="font-sans text-charcoal-500">No articles in this pathway yet — your Strategist is curating them.</p>
          <Link to="/client/messages" className="mt-3 inline-block font-sans text-[12px] font-semibold uppercase tracking-[0.22em] text-bronze-600 hover:text-navy-800">
            Ask your Strategist for a recommendation →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(r => (
            <Link key={r.id} to="/academy/resources/$slug" params={{ slug: r.slug }} className="block bg-white border border-bronze-600/20 rounded-[4px] p-5 shadow-soft hover:shadow-md transition-shadow">
              {r.category && <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">{r.category}</p>}
              <h3 className="mt-2 font-display text-navy-800 text-[18px] leading-snug">{r.title}</h3>
              {r.summary && <p className="mt-2 font-sans text-[13px] text-charcoal-500 line-clamp-3">{r.summary}</p>}
            </Link>
          ))}
        </div>
      )}
    </PortalPage>
  );
}